import React, { useEffect, useMemo, useState } from "react";
import {
  Panel,
  PanelType,
  Pivot,
  PivotItem,
  Spinner,
  Text,
  MessageBar,
  MessageBarType,
  SearchBox,
  PrimaryButton,
} from "@fluentui/react";
import {
  getUserAccessMap,
  downloadUserAccessReport,
  UserAccessApplication,
  UserAccessCustomer,
  UserAccessMap,
  UserAccessPrivilege,
  UserAccessRole,
} from "@/api/users";
import { saveAs } from "file-saver";

interface Props {
  userId: number | null;
  userEmail?: string;
  isOpen: boolean;
  onDismiss: () => void;
}

const badgeBaseStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
};

const assignedBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  color: "#107C10",
  backgroundColor: "#DFF6DD",
  border: "1px solid #A7D8A5",
};

const unassignedBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  color: "#A4262C",
  backgroundColor: "#FDE7E9",
  border: "1px solid #F1AEB5",
};

const UserAccessPanel = (props: Props) => {
  const { userId, userEmail, isOpen, onDismiss } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accessMap, setAccessMap] = useState<UserAccessMap | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [roleSearch, setRoleSearch] = useState<string>("");
  const [privilegeSearch, setPrivilegeSearch] = useState<string>("");
  const [downloading, setDownloading] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || userId == null) {
      return;
    }

    let isActive = true;
    setLoading(true);
    setError(null);

    getUserAccessMap(userId)
      .then((data) => {
        if (!isActive) {
          return;
        }
        setAccessMap(data || null);
        if (data && data.Applications && data.Applications.length > 0) {
          const firstApp = data.Applications[0];
          setSelectedAppId(firstApp.Id);
          if (firstApp.Customers && firstApp.Customers.length > 0) {
            setSelectedCustomerId(firstApp.Customers[0].Id);
          } else {
            setSelectedCustomerId(null);
          }
        } else {
          setSelectedAppId(null);
          setSelectedCustomerId(null);
        }
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setError("Failed to load user access information.");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, userId]);

  useEffect(() => {
    if (!isOpen) {
      setAccessMap(null);
      setSelectedAppId(null);
      setSelectedCustomerId(null);
      setRoleSearch("");
      setPrivilegeSearch("");
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const applications: UserAccessApplication[] = useMemo(
    () => accessMap?.Applications || [],
    [accessMap]
  );

  const selectedApplication = useMemo<UserAccessApplication | undefined>(() => {
    if (selectedAppId == null) {
      return undefined;
    }
    return applications.find((app) => app.Id === selectedAppId);
  }, [applications, selectedAppId]);

  const customers: UserAccessCustomer[] = useMemo(
    () => selectedApplication?.Customers || [],
    [selectedApplication]
  );

  const selectedCustomer = useMemo<UserAccessCustomer | undefined>(() => {
    if (selectedCustomerId == null) {
      return undefined;
    }
    return customers.find((customer) => customer.Id === selectedCustomerId);
  }, [customers, selectedCustomerId]);

  const handleAppTabClick = (item?: PivotItem) => {
    if (!item) {
      return;
    }
    const key = item.props.itemKey;
    if (!key) {
      return;
    }
    const id = Number(key);
    if (Number.isNaN(id)) {
      return;
    }
    setSelectedAppId(id);
    const app = applications.find((x) => x.Id === id);
    if (app && app.Customers && app.Customers.length > 0) {
      setSelectedCustomerId(app.Customers[0].Id);
    } else {
      setSelectedCustomerId(null);
    }
    setRoleSearch("");
    setPrivilegeSearch("");
  };

  const handleCustomerTabClick = (item?: PivotItem) => {
    if (!item) {
      return;
    }
    const key = item.props.itemKey;
    if (!key) {
      return;
    }
    const id = Number(key);
    if (Number.isNaN(id)) {
      return;
    }
    setSelectedCustomerId(id);
    setRoleSearch("");
    setPrivilegeSearch("");
  };

  const renderBadge = (isAssigned: boolean) => {
    const style = isAssigned ? assignedBadgeStyle : unassignedBadgeStyle;
    return (
      <span style={style}>{isAssigned ? "Assigned" : "Not assigned"}</span>
    );
  };

  const renderRoles = (roles: UserAccessRole[]) => {
    const term = roleSearch.trim().toLowerCase();
    const filteredRoles = term
      ? roles.filter((role) => {
          const name = role.Name.toLowerCase();
          const description = (role.Description || "").toLowerCase();
          return name.includes(term) || description.includes(term);
        })
      : roles;

    if (!filteredRoles || filteredRoles.length === 0) {
      return <Text>No roles defined for this application and customer.</Text>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filteredRoles.map((role) => (
          <div
            key={role.Id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 8px",
              borderBottom: "1px solid #edebe9",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text variant="mediumPlus">{role.Name}</Text>
              {role.Description && (
                <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
                  {role.Description}
                </Text>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {renderBadge(role.IsAssigned)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPrivileges = (privileges: UserAccessPrivilege[]) => {
    const term = privilegeSearch.trim().toLowerCase();
    const filteredPrivileges = term
      ? privileges.filter((privilege) => {
          const name = privilege.Name.toLowerCase();
          const description = (privilege.Description || "").toLowerCase();
          return name.includes(term) || description.includes(term);
        })
      : privileges;

    if (!filteredPrivileges || filteredPrivileges.length === 0) {
      return <Text>No privileges defined for this application and customer.</Text>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filteredPrivileges.map((privilege) => (
          <div
            key={privilege.Id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 8px",
              borderBottom: "1px solid #edebe9",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text variant="mediumPlus">{privilege.Name}</Text>
              {privilege.Description && (
                <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
                  {privilege.Description}
                </Text>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {renderBadge(privilege.IsAssigned)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleDownloadReport = async () => {
    if (userId == null) {
      return;
    }

    try {
      setDownloading(true);
      const response = await downloadUserAccessReport(userId);
      const contentType = response.headers["content-type"] || "text/html";
      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers["content-disposition"] as string | undefined;
      let fileName = userEmail ? `user-access-${userEmail}.html` : `user-access-${userId}.html`;
      if (disposition) {
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      saveAs(blob, fileName);
    } catch {
      setError("Failed to download user access report.");
    } finally {
      setDownloading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <Spinner label="Loading access information..." />;
    }

    if (error) {
      return (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
          {error}
        </MessageBar>
      );
    }

    if (!accessMap || !applications || applications.length === 0) {
      return <Text>This user does not have access to any applications.</Text>;
    }

    if (!selectedApplication) {
      return <Text>Select an application to view access details.</Text>;
    }

    if (!customers || customers.length === 0) {
      return <Text>No customers found for this application.</Text>;
    }

    if (!selectedCustomer) {
      return <Text>Select a customer to view roles and privileges.</Text>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <Text variant="mediumPlus">Applications</Text>
          <Pivot
            selectedKey={selectedAppId != null ? selectedAppId.toString() : undefined}
            onLinkClick={handleAppTabClick}
          >
            {applications.map((app) => (
              <PivotItem
                headerText={app.Name}
                itemKey={app.Id.toString()}
                key={app.Id}
              />
            ))}
          </Pivot>
        </div>
        <div>
          <Text variant="mediumPlus">Customers</Text>
          <Pivot
            selectedKey={
              selectedCustomerId != null ? selectedCustomerId.toString() : undefined
            }
            onLinkClick={handleCustomerTabClick}
          >
            {customers.map((customer) => (
              <PivotItem
                headerText={customer.Name}
                itemKey={customer.Id.toString()}
                key={customer.Id}
              />
            ))}
          </Pivot>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text variant="large">Roles</Text>
            <SearchBox
              placeholder="Filter roles"
              value={roleSearch}
              onChange={(_, newValue) => setRoleSearch(newValue || "")}
              styles={{ root: { marginTop: 8 } }}
            />
            <div style={{ marginTop: 8 }}>
              {renderRoles(selectedCustomer.Roles || [])}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text variant="large">Privileges</Text>
            <SearchBox
              placeholder="Filter privileges"
              value={privilegeSearch}
              onChange={(_, newValue) => setPrivilegeSearch(newValue || "")}
              styles={{ root: { marginTop: 8 } }}
            />
            <div style={{ marginTop: 8 }}>
              {renderPrivileges(selectedCustomer.Privileges || [])}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.large}
      headerText={userEmail ? `View Access - ${userEmail}` : "View Access"}
      closeButtonAriaLabel="Close"
      isLightDismiss
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Text>
            View this user's access across applications, customers, roles, and
            privileges.
          </Text>
          <PrimaryButton
            text={downloading ? "Downloading..." : "Download report"}
            iconProps={{ iconName: "Download" }}
            onClick={handleDownloadReport}
            disabled={loading || downloading || userId == null}
          />
        </div>
        {renderContent()}
      </div>
    </Panel>
  );
};

export default UserAccessPanel;
